import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t.nav.accueil, testid: "nav-accueil" },
    { to: "/carte-restaurant", label: t.nav.restaurant, testid: "nav-carte-restaurant" },
    { to: "/carte-petit-dejeuner", label: t.nav.petitDejeuner, testid: "nav-carte-petit-dejeuner" },
    { to: "/carte-gourmande", label: t.nav.gourmande, testid: "nav-carte-gourmande" },
  ];

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 16);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 transition-all duration-500">
        <div
          className={`glass-light rounded-full flex items-center justify-between pl-4 pr-2 sm:pl-6 py-2 transition-all duration-500 ${
            scrolled ? "shadow-[0_10px_40px_rgba(44,30,22,0.10)]" : ""
          }`}
        >
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2 group">
            <span
              className="font-[\'Playfair_Display\'] text-xl sm:text-2xl tracking-tight whitespace-nowrap"
              style={{ color: "var(--color-dark)" }}
            >
              La Caravelle
            </span>
            <span
              className="hidden sm:inline-block h-1 w-1 rounded-full"
              style={{ background: "var(--color-accent)" }}
            />
            <span
              className="hidden sm:inline text-[10px] uppercase tracking-[0.25em]"
              style={{ color: "var(--color-text-muted)" }}
            >
              {t.common.narbonne}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm rounded-full transition-all ${
                    isActive
                      ? "text-[color:var(--color-bg)] bg-[color:var(--color-dark)]"
                      : "text-[color:var(--color-dark)] hover:bg-white/50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <LanguageSwitcher />
            <div className="hidden lg:block">
              <a
                href="tel:0468325017"
                data-testid="header-phone"
                className="btn-pill btn-dark"
              >
                <Phone size={15} strokeWidth={2} />
                04 68 32 50 17
              </a>
            </div>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              aria-label="Menu"
              className="lg:hidden h-10 w-10 rounded-full flex items-center justify-center text-[color:var(--color-dark)] hover:bg-white/60 transition"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden mt-3 glass-light rounded-3xl p-4 flex flex-col gap-1"
          >
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-2xl text-sm transition ${
                    isActive
                      ? "text-[color:var(--color-bg)] bg-[color:var(--color-dark)]"
                      : "text-[color:var(--color-dark)] hover:bg-white/60"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href="tel:0468325017"
              data-testid="mobile-phone"
              className="mt-2 btn-pill btn-dark justify-center"
            >
              <Phone size={15} /> 04 68 32 50 17
            </a>
          </div>
        )}
      </div>
    </header>
  );
};
